import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ProfileCard from './ProfileCard';
import FullProfileModal from './FullProfileModal';
import ImageLightbox from './ImageLightbox';

// Lets any avatar or name anywhere in the app open:
// - the profile card (a popover next to what was clicked; a bottom sheet on
//   phones),
// - the full profile (a modal with mutual friends/groups in full),
// - the image lightbox (a photo at full size).
// One of each at a time, rendered here so they float above whatever page
// they were opened from and close on navigation.
const ProfileContext = createContext(null);

// Outside the provider (meeting screen, admin) avatars still render, they
// just don't open anything.
const NOOP = { openProfile: () => {}, openFullProfile: () => {}, openImage: () => {}, closeProfile: () => {} };

export function ProfileProvider({ children }) {
  const [card, setCard] = useState(null); // { username, rect, seed, key }
  const [full, setFull] = useState(null); // username
  const [image, setImage] = useState(null); // { imageUrl, alt }
  const location = useLocation();

  // anchor: the clicked element - the card is placed next to it. seed: what
  // the caller already knows (name, avatar), shown while the rest loads.
  const openProfile = useCallback((username, anchor, seed) => {
    if (!username) return;
    const rect = anchor && anchor.getBoundingClientRect ? anchor.getBoundingClientRect() : null;
    setCard({ username, rect, seed, key: Date.now() });
  }, []);
  const closeProfile = useCallback(() => setCard(null), []);
  const openFullProfile = useCallback((username) => {
    setCard(null);
    setFull(username);
  }, []);
  const openImage = useCallback((imageUrl, alt) => {
    if (imageUrl) setImage({ imageUrl, alt });
  }, []);

  useEffect(() => {
    setCard(null);
    setFull(null);
    setImage(null);
  }, [location.pathname]);

  const value = useMemo(() => ({ openProfile, openFullProfile, openImage, closeProfile }), [openProfile, openFullProfile, openImage, closeProfile]);

  return (
    <ProfileContext.Provider value={value}>
      {children}
      {card && <ProfileCard key={card.key} {...card} onClose={closeProfile} />}
      {full && <FullProfileModal username={full} onClose={() => setFull(null)} />}
      {image && <ImageLightbox {...image} onClose={() => setImage(null)} />}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  return useContext(ProfileContext) || NOOP;
}
